import React from "react";
import { __t } from "./translate";
import { useOptions } from "@/hooks/useOptions";
import { getPrices } from "./utils";
import { Review } from "./types";
import { useCategories } from "@/hooks/useCategories";

export const Schema = ({ data }) => {
    let breadcrumbList = {};
    let schemaBody = {};
    let faqBody = {};
    let offers = [];
    let reviews = [];
    let breadcrumbItems = [
        {
            "@type": "ListItem",
            "position": 1,
            "item": {
                "name": __t("Home"),
                "@id": process.env.GATSBY_SITE_URL
            }
        }
    ];

    // categories pages
    if (data.allCategories?.length > 0 && data.category) {
        breadcrumbItems.push({
            "@type": "ListItem",
            "position": 2,
            "item": {
                "name": data.category.name + ` | ${__t("Ortorex™ - Orthopedic Treatment from Head to Toe")}`,
                "@id": `${process.env.GATSBY_SITE_URL}/product-category/${data.category.slug}`
            }
        })
    }

    // shop / cart / blog pages
    if (data.slug && (data.slug === '/shop/' || data.slug === '/cart/' || data.slug === '/blog/')) {
        breadcrumbItems.push({
            "@type": "ListItem",
            "position": 2,
            "item": {
                "name": data.name,
                "@id": `${process.env.GATSBY_SITE_URL}${data.slug}`
            }
        })
    }

    // default pages
    if (data.page && data.page.status === 'publish') {
        breadcrumbItems.push({
            "@type": "ListItem",
            "position": 2,
            "item": {
                "name": data.page.title.rendered,
                "@id": `${process.env.GATSBY_SITE_URL}/${data.page.slug}`
            }
        })
    }

    // blog pages
    if (data.page && data.page.type === 'post') {
        const allCategories = useCategories().allWpCategories.nodes[0].data;
        const category = allCategories.find((c) => c.id === data.page.categories[0]);

        breadcrumbItems.push(
            {
                "@type": "ListItem",
                "position": 2,
                "item": {
                    "name": category.name,
                    "@id": `${process.env.GATSBY_SITE_URL}/blog/${category.slug}`
                }
            },
            {
                "@type": "ListItem",
                "position": 3,
                "item": {
                    "name": data.page.title.rendered,
                    "@id": `${process.env.GATSBY_SITE_URL}${data.slug}`
                }
            }
        )
    }

    // blog category pages
    if (data.blogCategory) {
        breadcrumbItems.push(
            {
                "@type": "ListItem",
                "position": 2,
                "item": {
                    "name": data.pageTitle,
                    "@id": `${process.env.GATSBY_SITE_URL}${data.pageContext.slug}`
                }
            }
        )
    }

    // tag pages
    if (data.allTags) {
        breadcrumbItems.push({
            "@type": "ListItem",
            "position": 2,
            "item": {
                "name": data.tag.name,
                "@id": `${process.env.GATSBY_SITE_URL}/${data.slug}`
            }
        })
    }

    // FAQ page
    if (data.page && data.page.slug === 'faq') {
        let faqItems = [];

        data.page.acf.faqs.map((faq) => (

            faq.items.map((item: any) => {
                const question = item.title ? item.title : '';
                const answer = item.content ? item.content : '';

                faqItems.push({
                    "@type": "Question",
                    "url": `${process.env.GATSBY_SITE_URL}/${data.page.slug}/#hfaq-post-${item.post_id}`,
                    "name": question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": answer
                    }
                })
            })
        ))

        faqBody = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems
        }
    }

    // product page schema
    if (data.product) {
        const product = data.product;

        const options = useOptions();
        const currency = options.wpOptions.data.currency;

        const prices = getPrices(product);

        if (product.variations_product.length > 0 && prices?.min_sale_price === prices?.max_sale_price) {
            offers.push(
                {
                    "@type": "AggregateOffer",
                    "lowPrice": prices.min_sale_price,
                    "highPrice": prices.max_sale_price,
                    "offerCount": product.variations_product.length,
                    "priceCurrency": currency,
                    "availability": product.stock_status === 'outofstock' ? "http://schema.org/OutOfStock" : "http://schema.org/InStock",
                    "url": `${process.env.GATSBY_SITE_URL}/p/${product.slug}/`,
                    "seller": {
                        "@type": "Organization",
                        "name": __t("Ortorex™ - Orthopedic Treatment from Head to Toe"),
                        "url": process.env.GATSBY_SITE_URL
                    },
                    "sku": product.sku,
                    "itemCondition": "https://schema.org/NewCondition",
                    "mpn": product.wordpress_id
                }
            )
        } else {
            offers.push(
                {
                    "@type": "Offer",
                    "price": product.sale_price ? product.sale_price : product.price,
                    "priceValidUntil": `${new Date().getFullYear() + 1}-12-31`,
                    "priceSpecification": {
                        "price": product.sale_price ? product.sale_price : product.price,
                        "priceCurrency": currency,
                        "valueAddedTaxIncluded": "false"
                    },
                    "priceCurrency": currency,
                    "availability": product.stock_status === 'outofstock' ? "http://schema.org/OutOfStock" : "http://schema.org/InStock",
                    "url": `${process.env.GATSBY_SITE_URL}/p/${product.slug}/`,
                    "seller": {
                        "@type": "Organization",
                        "name": __t("Ortorex™ - Orthopedic Treatment from Head to Toe"),
                        "url": process.env.GATSBY_SITE_URL
                    }
                }
            )
        }

        breadcrumbItems.push({
            "@type": "ListItem",
            "position": 2,
            "item": {
                "name": product.categories.length > 0 ? product.categories[0].name : 'Uncategorized',
                "@id": `${process.env.GATSBY_SITE_URL}/product-category/${product.categories.length > 0 ? product.categories[0].slug : ''}`
            }
        },
            {
                "@type": "ListItem",
                "position": 3,
                "item": {
                    "name": product.name,
                    "@id": `${process.env.GATSBY_SITE_URL}/p/${product.slug}`
                }
            });

        data.allWcReviews?.edges.forEach((review: Review) => {
            reviews.push({
                "@type": "Review",
                "reviewRating": {
                    "@type": "Rating",
                    "bestRating": "5",
                    "ratingValue": `${review.node.rating}`,
                    "worstRating": "1"
                },
                "author": {
                    "@type": "Person",
                    "name": review.node.reviewer
                },
                "reviewBody": review.node.review,
                "datePublished": review.node.date_created
            });
        });

        schemaBody = {
            "@context": "https://schema.org",
            "@type": "Product",
            "@id": `${process.env.GATSBY_SITE_URL}/p/${product.slug}/#product`,
            "name": product.name,
            "url": `${process.env.GATSBY_SITE_URL}/p/${product.slug}/`,
            "description": product.description,
            "image": `${process.env.GATSBY_SITE_URL}${product.image.localFile?.childrenImageSharp?.length ? product.image.localFile.childrenImageSharp[0].gatsbyImageData.images.fallback.src : product.image.src}`,
            "sku": product.sku,
            "offers": offers,
            "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": product.average_rating,
                "reviewCount": product.rating_count
            },
            "review": reviews,
            "brand": "Ortorex",
            "mpn": product.sku
        }

        if (product.acf?.faq && product.acf.faq.items.length > 0) {
            let faqItems = [];

            product.acf.faq.items.map((item: any, index: number) => {
                const question = item.title ? item.title : '';
                const answer = item.content ? item.content : '';

                faqItems.push({
                    "@type": "Question",
                    "url": `${process.env.GATSBY_SITE_URL}/p/${product.slug}/#hfaq-post-${index}`,
                    "name": question,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": answer
                    }
                })
            })

            faqBody = {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqItems
            }
        }
    }

    breadcrumbList = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
    }

    const jsonData = {
        "@context": "https://schema.org/",
        "@graph": [breadcrumbList, schemaBody, faqBody]
    };

    return (
        <script type="application/ld+json">{JSON.stringify(jsonData)}</script>
    );
};